/**
 * Version History panel — the full-height left column shown in Compare mode.
 *
 * Replaces the two compare `<select>` dropdowns with a scrollable list of
 * version cards (name + date + author). Selecting two versions drives the
 * redline; each card has inline rename (pencil → input with ✕/✓), matching the
 * inline-edit pattern used elsewhere in the wizard.
 *
 * It is presentational: the parent (CompareVersionsPanel) owns which two
 * versions are selected and performs the compare + rename side effects.
 */

import React, { useState } from 'react';
import { ReactComponent as EditIcon } from '../../../../shared/icons/edit.svg';
import './versionHistoryPanel.scss';
import type { CompareVersion } from './compareTypes';

export function VersionHistoryPanel({
  versions,
  baseKey,
  revKey,
  busy = false,
  singleSelect = false,
  onSelect,
  onRename,
}: {
  versions: CompareVersion[];
  /** The two currently-selected version keys (base = A/older, rev = B/newer).
   *  In singleSelect mode only `baseKey` is used (the active version). */
  baseKey: string;
  revKey: string;
  /** True while a comparison / version load is running (drives the header hint). */
  busy?: boolean;
  /** VIEW mode: click a card to VIEW that version (one active card, no A/B
   *  compare semantics, no A/B badges or "select A/B" hint). Used for viewing
   *  the version history of an executed deal doc read-only. */
  singleSelect?: boolean;
  /** Click a card. Compare mode advances A→B; single-select mode just views. */
  onSelect: (key: string) => void;
  /** Persist a renamed version. Returns a promise so the row can show progress.
   *  Absent for versions that can't be renamed (e.g. "Current"). */
  onRename?: (version: CompareVersion, name: string) => Promise<void>;
}) {
  // Which slot the NEXT click fills — drives the "Select version A/B" hint.
  const nextSlot: 'A' | 'B' = !baseKey || (baseKey && revKey) ? 'A' : 'B';
  const hint = busy
    ? singleSelect
      ? 'Loading…'
      : 'Comparing…'
    : singleSelect
      ? 'Click a version to view'
      : nextSlot === 'A'
        ? 'Select version A'
        : 'Now select version B';
  return (
    <aside style={PANEL} aria-label="Version history">
      {/* Title + inline instruction, right-aligned. */}
      <div style={HEADER_ROW}>
        <span style={HEADER_TITLE}>Versions</span>
        <span style={HINT}>{hint}</span>
      </div>
      <div style={LIST}>
        {versions.map((v) => (
          <VersionCard
            key={v.key}
            version={v}
            selected={singleSelect ? v.key === baseKey : v.key === baseKey || v.key === revKey}
            // No A/B badge in view mode — there's only one active version.
            role={
              singleSelect
                ? undefined
                : v.key === baseKey
                  ? 'base'
                  : v.key === revKey
                    ? 'rev'
                    : undefined
            }
            onSelect={() => onSelect(v.key)}
            onRename={onRename}
          />
        ))}
      </div>
    </aside>
  );
}

function VersionCard({
  version,
  selected,
  role,
  onSelect,
  onRename,
}: {
  version: CompareVersion;
  selected: boolean;
  role?: 'base' | 'rev';
  onSelect: () => void;
  onRename?: (version: CompareVersion, name: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(version.label);
  const [saving, setSaving] = useState(false);

  // "Current" and the pre-migration snapshot are synthetic — not renameable.
  const renameable = !!onRename && version.key.startsWith('revision-');

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(version.label);
    setEditing(true);
  };

  const cancel = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditing(false);
  };

  const save = async (e?: React.MouseEvent) => {
    e?.stopPropagation();
    const name = draft.trim();
    if (!name || !onRename) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onRename(version, name);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{ ...CARD, ...(selected ? CARD_SELECTED : null) }}
      onClick={editing ? undefined : onSelect}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
    >
      {role && <span style={ROLE_BADGE}>{role === 'base' ? 'A' : 'B'}</span>}

      <div style={{ minWidth: 0, flex: 1 }}>
        {editing ? (
          <div style={EDIT_ROW} onClick={(e) => e.stopPropagation()}>
            <input
              autoFocus
              value={draft}
              disabled={saving}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void save();
                if (e.key === 'Escape') cancel();
              }}
              style={EDIT_INPUT}
              aria-label="Version name"
            />
            <button style={ICON_BTN} onClick={cancel} title="Cancel" aria-label="Cancel" type="button">
              ✕
            </button>
            <button
              style={{ ...ICON_BTN, color: '#137333' }}
              onClick={save}
              disabled={saving || !draft.trim()}
              title="Save"
              aria-label="Save"
              type="button"
            >
              ✓
            </button>
          </div>
        ) : (
          <>
            <div style={NAME_ROW}>
              <span style={NAME} title={version.label}>
                {version.label}
              </span>
              {renameable && (
                <button
                  className="version-card__rename-btn"
                  onClick={startEdit}
                  title="Rename version"
                  aria-label="Rename version"
                  type="button"
                >
                  <EditIcon />
                </button>
              )}
            </div>
            {version.sublabel && <div style={SUBLABEL}>{version.sublabel}</div>}
          </>
        )}
      </div>
    </div>
  );
}

const PANEL: React.CSSProperties = {
  width: 260,
  flexShrink: 0,
  borderRight: '1px solid #e6e9ee',
  background: '#fff',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
};
const HEADER_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '14px 12px',
  borderBottom: '1px solid #eef1f4',
};
const HEADER_TITLE: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '.02em',
  textTransform: 'uppercase',
  color: '#5f6368',
};
const HINT: React.CSSProperties = {
  fontSize: 12,
  color: '#006c90',
  fontWeight: 500,
  whiteSpace: 'nowrap',
};
const LIST: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const CARD: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  padding: '10px 12px',
  border: '1px solid #e6e9ee',
  borderRadius: 8,
  cursor: 'pointer',
  background: '#fff',
  transition: 'border-color .12s, box-shadow .12s',
};
const CARD_SELECTED: React.CSSProperties = {
  borderColor: '#006c90',
  boxShadow: '0 0 0 1px #006c90 inset',
  background: '#f2fafd',
};
const ROLE_BADGE: React.CSSProperties = {
  flexShrink: 0,
  width: 20,
  height: 20,
  borderRadius: 4,
  background: '#006c90',
  color: '#fff',
  fontSize: 11,
  fontWeight: 700,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 1,
};
const NAME_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  justifyContent: 'space-between',
};
const NAME: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: '#202124',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const SUBLABEL: React.CSSProperties = { fontSize: 12, color: '#5f6368', marginTop: 2 };
const EDIT_ROW: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 4 };
const EDIT_INPUT: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  height: 28,
  padding: '0 8px',
  border: '1px solid #006c90',
  borderRadius: 5,
  fontSize: 13,
};
const ICON_BTN: React.CSSProperties = {
  flexShrink: 0,
  width: 26,
  height: 26,
  border: '1px solid #d6dae0',
  borderRadius: 5,
  background: '#fff',
  cursor: 'pointer',
  fontSize: 13,
};
